import { Navigate, Route, Routes, useLocation } from "react-router";
import { API_BASE } from "./api/base";
import { useAuth } from "./auth/OAuthContext";
import { LandingPage } from "./pages/LandingPage";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { MfaEnrollment } from "./pages/MfaEnrollment";
import { NurseDashboard } from "./pages/NurseDashboard";
import { DoctorQueue } from "./pages/DoctorQueue";
import { DocumentReview } from "./pages/DocumentReview";
import { AdminUsers } from "./pages/AdminUsers";
import { NurseSignup } from "./pages/NurseSignup";
import { AcceptInvite } from "./pages/AcceptInvite";
import { ResetPassword } from "./pages/ResetPassword";
import { SealVerificationPage } from "./pages/SealVerificationPage";
import { VerifiedDocumentRedirect } from "./pages/VerifiedDocumentRedirect";
import { DocumentsPage } from "./pages/DocumentsPage";
import { BrandLogo } from "./components/BrandLogo";

const PUBLIC_LINK_PATHS = new Set([
  "/",
  "/register",
  "/signup/nurse",
  "/accept-invite",
  "/reset-password",
]);

const ENTRA_SSO_ENABLED = import.meta.env.VITE_ENTRA_SSO_ENABLED === "true";

function isPublicLinkPath(pathname: string): boolean {
  return PUBLIC_LINK_PATHS.has(pathname) || isPublicVerifyPath(pathname);
}

export function App() {
  const { idToken, roles, mfaEnrolled, loading } = useAuth();
  const location = useLocation();
  const landing = appLandingForRoles(roles);
  const dashboard = roles.includes("Doctor")
    ? <DoctorQueue />
    : roles.includes("Nurse")
      ? <NurseDashboard />
      : roles.includes("Admin")
        ? <AdminUsers />
        : <Navigate to="/unauthorized" replace />;

  if (location.pathname === "/login") {
    if (loading) return <AuthLoading />;
    if (idToken && !mfaEnrolled) return <MfaEnrollment />;
    if (idToken) return <Navigate to="/dashboard" replace />;
    return <Login />;
  }

  if (isPublicLinkPath(location.pathname)) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/signup/nurse" element={<NurseSignup />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify/:sealToken" element={<SealVerificationPage />} />
      </Routes>
    );
  }

  if (loading) return <AuthLoading />;

  if (idToken && !mfaEnrolled) {
    return <MfaEnrollment />;
  }

  if (!idToken) {
    if (ENTRA_SSO_ENABLED && isProtectedVerifyOpenPath(location.pathname)) {
      window.location.assign(`${API_BASE}/entra/login?returnTo=${encodeURIComponent("/dashboard")}`);
      return <AuthLoading />;
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route path="/dashboard" element={dashboard} />
      <Route path="/upload" element={<Navigate to="/dashboard" replace />} />
      <Route path="/queue" element={<Navigate to="/dashboard" replace />} />
      <Route path="/documents" element={<DocumentsPage />} />
      <Route path="/admin" element={<AdminUsers />} />
      <Route path="/documents/:id" element={<DocumentReview />} />
      <Route path="/verify/:sealToken/open" element={<VerifiedDocumentRedirect />} />
      <Route
        path="/unauthorized"
        element={
          <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 text-slate-700">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-md text-center">
              <h2 className="text-lg font-bold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-sm font-medium leading-relaxed text-slate-600">
                Your account has no Nurse or Doctor role assigned. Contact an
                administrator.
              </p>
            </div>
          </div>
        }
      />
      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}

function appLandingForRoles(roles: string[]): string {
  return roles.some((role) => role === "Doctor" || role === "Nurse" || role === "Admin")
    ? "/dashboard"
    : "/unauthorized";
}

function isPublicVerifyPath(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  return parts.length === 2 && parts[0] === "verify" && parts[1] !== "open";
}

function isProtectedVerifyOpenPath(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  return parts.length === 3 && parts[0] === "verify" && parts[2] === "open";
}

function AuthLoading() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 p-6" role="status" aria-live="polite">
      <div className="flex flex-col items-center text-center max-w-xs">
        <BrandLogo className="h-10 w-auto mb-6" />
        <div className="w-9 h-9 border-3 border-indigo-100 border-t-[#012699] rounded-full animate-spin mb-4" aria-hidden="true" />
        <h1 className="text-xl font-bold text-slate-900 tracking-tight mb-1 font-display">MEATrix OrderFlow</h1>
        <p className="text-sm text-slate-500">Getting your workspace ready...</p>
      </div>
    </main>
  );
}
